import Head from 'next/head'
import { SITE_NAME, SeoData, TWITTER_HANDLE, absoluteUrl } from '../../utils/seo'

/**
 * The single <Head> renderer for the whole site.
 *
 * - Canonical is built from route params (never window.location, which leaks
 *   query strings and breaks on the server).
 * - og:title / og:image / description reuse the same `key` values as the
 *   app-wide defaults in _app.tsx so Next dedupes them instead of shipping two
 *   competing copies. Any tag a page may override needs a `key`.
 * - og:image is always absolute — Facebook, LINE and X reject relative paths,
 *   which is why share previews used to fall back to nothing on listing pages.
 * - The site serves one URL per page with mixed Thai/English content and no
 *   locale routing, so no hreflang alternates are emitted (a same-URL
 *   en/th/x-default trio is invalid); og:locale declares the primary locale.
 * - `max-image-preview:large` opts into large SERP/Discover thumbnails, which
 *   is the main CTR lever for a mobile-first Thai games site.
 */
const PageSeo = ({ seo }: { seo: SeoData }) => {
  const url = absoluteUrl(seo.canonicalPath)
  return (
    <Head>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} key="description" />
      {!!seo.keywords?.length && <meta name="keywords" content={seo.keywords.join(', ')} />}
      <meta
        name="robots"
        content={
          seo.noindex
            ? 'noindex, follow'
            : 'index, follow, max-image-preview:large, max-snippet:-1'
        }
        key="robots"
      />
      <link rel="canonical" href={url} key="canonical" />

      <meta property="og:type" content={seo.ogType} key="og:type" />
      <meta property="og:site_name" content={SITE_NAME} key="og:site_name" />
      <meta property="og:title" content={seo.title} key="title" />
      <meta property="og:description" content={seo.description} key="og:description" />
      <meta property="og:url" content={url} key="og:url" />
      <meta property="og:locale" content="th_TH" key="og:locale" />
      <meta property="og:image" content={seo.ogImage} key="image" />
      <meta property="og:image:alt" content={seo.ogImageAlt} key="og:image:alt" />

      <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
      <meta name="twitter:site" content={TWITTER_HANDLE} key="twitter:site" />
      <meta name="twitter:title" content={seo.title} key="twitter:title" />
      <meta name="twitter:description" content={seo.description} key="twitter:description" />
      <meta name="twitter:image" content={seo.ogImage} key="twitter:image" />

      {(seo.jsonLd ?? []).map((schema, i) => (
        <script
          key={`jsonld-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Head>
  )
}

export default PageSeo
